import { SEO } from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, AlertTriangle, Clock, Users, FileText, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";

const IncidentResponsePolicy = () => {
  const navigate = useNavigate();
  const lastUpdated = "20 décembre 2024";
  const companyName = "EasyFinder";
  const securityEmail = "security@easyfinder.fr";
  const dpoEmail = "dpo@easyfinder.fr";
  const contactEmail = "contact@easyfinder.fr";

  return (
    <>
      <SEO
        title="Politique de Réponse aux Incidents | EasyFinder"
        description="Politique de réponse aux incidents de sécurité d'EasyFinder. Procédures de détection, investigation et résolution des incidents."
        canonical="/incident-response-policy"
      />
      <div className="min-h-screen bg-background">
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour
            </Button>
            <LanguageSwitcher frPath="/incident-response-policy" enPath="/incident-response-policy-en" />
          </div>

          <article className="prose prose-slate dark:prose-invert max-w-none">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold mb-0">Politique de Réponse aux Incidents de Sécurité</h1>
            </div>
            <p className="text-muted-foreground mb-8">
              Dernière mise à jour : {lastUpdated}
            </p>

            {/* Purpose */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <FileText className="h-6 w-6" />
                1. Objet
              </h2>
              <p>
                Cette politique définit les procédures de détection, signalement, investigation et résolution 
                des incidents de sécurité informatique affectant {companyName} et ses utilisateurs.
              </p>
              <p>
                L'objectif est d'assurer une réponse rapide, coordonnée et efficace à tout incident 
                susceptible de compromettre la confidentialité, l'intégrité ou la disponibilité des données 
                et systèmes d'{companyName}.
              </p>
            </section>

            {/* Scope */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">2. Périmètre d'Application</h2>
              <p>Cette politique s'applique à :</p>
              <ul>
                <li>Tous les systèmes et infrastructures d'{companyName} (application web, API, base de données)</li>
                <li>Tous les employés, prestataires et partenaires ayant accès aux systèmes</li>
                <li>Tous les sous-traitants traitant des données pour le compte d'{companyName}</li>
                <li>Les données personnelles des utilisateurs conformément au RGPD</li>
              </ul>
              
              <h3 className="text-xl font-medium mt-6 mb-3">Systèmes couverts</h3>
              <table className="w-full border-collapse border border-border mt-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">Système</th>
                    <th className="border border-border p-3 text-left">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3">Application Web</td>
                    <td className="border border-border p-3">easyfinder.fr - Interface utilisateur principale</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">API Backend</td>
                    <td className="border border-border p-3">Supabase Edge Functions - Logique métier</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Base de données</td>
                    <td className="border border-border p-3">PostgreSQL (Supabase) - Stockage des données</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Stockage fichiers</td>
                    <td className="border border-border p-3">Supabase Storage - Documents utilisateurs</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Authentification</td>
                    <td className="border border-border p-3">Supabase Auth + OAuth Google</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">CDN / DNS</td>
                    <td className="border border-border p-3">Cloudflare - Distribution et protection</td>
                  </tr>
                </tbody>
              </table>
            </section>

            {/* Roles and Responsibilities */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Users className="h-6 w-6" />
                3. Rôles et Responsabilités
              </h2>

              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-2">Équipe de Réponse aux Incidents (IRT)</h3>
                <table className="w-full">
                  <tbody>
                    <tr>
                      <td className="py-2 font-medium">Responsable Sécurité (Security Officer)</td>
                      <td className="py-2">{securityEmail}</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-medium">Délégué à la Protection des Données (DPO)</td>
                      <td className="py-2">{dpoEmail}</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-medium">Contact Communication</td>
                      <td className="py-2">{contactEmail}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-medium mt-6 mb-3">Responsabilités par rôle</h3>
              
              <h4 className="text-lg font-medium mt-4 mb-2">Responsable Sécurité</h4>
              <ul>
                <li>Coordonner toutes les activités de réponse aux incidents</li>
                <li>Prendre les décisions critiques (isolation, restauration, notification)</li>
                <li>Communiquer avec la direction et les parties prenantes</li>
                <li>Superviser l'analyse forensique et la documentation</li>
                <li>Valider le rapport post-incident et les mesures correctives</li>
              </ul>

              <h4 className="text-lg font-medium mt-4 mb-2">DPO (Délégué à la Protection des Données)</h4>
              <ul>
                <li>Évaluer l'impact sur les données personnelles (RGPD)</li>
                <li>Préparer les notifications CNIL (dans les 72h si nécessaire)</li>
                <li>Coordonner la communication aux personnes concernées</li>
                <li>Documenter les violations de données</li>
              </ul>

              <h4 className="text-lg font-medium mt-4 mb-2">Équipe Technique</h4>
              <ul>
                <li>Détecter et analyser les indicateurs de compromission</li>
                <li>Isoler les systèmes affectés</li>
                <li>Collecter les preuves (logs, captures)</li>
                <li>Implémenter les correctifs et restaurer les services</li>
              </ul>

              <h4 className="text-lg font-medium mt-4 mb-2">Tous les collaborateurs</h4>
              <ul>
                <li>Signaler immédiatement tout incident suspecté</li>
                <li>Ne pas tenter de résoudre seul un incident de sécurité</li>
                <li>Préserver les preuves (ne pas supprimer de logs ou fichiers)</li>
              </ul>
            </section>

            {/* Incident Classification */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="h-6 w-6" />
                4. Classification des Incidents
              </h2>

              <table className="w-full border-collapse border border-border mt-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">Niveau</th>
                    <th className="border border-border p-3 text-left">Description</th>
                    <th className="border border-border p-3 text-left">Exemples</th>
                    <th className="border border-border p-3 text-left">Délai de réponse</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-red-50 dark:bg-red-950">
                    <td className="border border-border p-3 font-bold text-red-600 dark:text-red-400">🔴 Critique (P1)</td>
                    <td className="border border-border p-3">Compromission active, fuite de données confirmée</td>
                    <td className="border border-border p-3">
                      <ul className="list-disc ml-4 text-sm">
                        <li>Accès non autorisé à la base de données</li>
                        <li>Fuite de données personnelles</li>
                        <li>Ransomware</li>
                      </ul>
                    </td>
                    <td className="border border-border p-3 font-bold">Immédiat (≤1h)</td>
                  </tr>
                  <tr className="bg-orange-50 dark:bg-orange-950">
                    <td className="border border-border p-3 font-bold text-orange-600 dark:text-orange-400">🟠 Élevé (P2)</td>
                    <td className="border border-border p-3">Tentative d'intrusion, vulnérabilité exploitable</td>
                    <td className="border border-border p-3">
                      <ul className="list-disc ml-4 text-sm">
                        <li>Attaque DDoS en cours</li>
                        <li>Tentatives de brute force</li>
                        <li>Vulnérabilité 0-day découverte</li>
                      </ul>
                    </td>
                    <td className="border border-border p-3 font-bold">≤4h</td>
                  </tr>
                  <tr className="bg-yellow-50 dark:bg-yellow-950">
                    <td className="border border-border p-3 font-bold text-yellow-600 dark:text-yellow-400">🟡 Moyen (P3)</td>
                    <td className="border border-border p-3">Activité suspecte, anomalie détectée</td>
                    <td className="border border-border p-3">
                      <ul className="list-disc ml-4 text-sm">
                        <li>Pic d'erreurs d'authentification</li>
                        <li>Scan de ports détecté</li>
                        <li>Comportement utilisateur anormal</li>
                      </ul>
                    </td>
                    <td className="border border-border p-3 font-bold">≤24h</td>
                  </tr>
                  <tr className="bg-blue-50 dark:bg-blue-950">
                    <td className="border border-border p-3 font-bold text-blue-600 dark:text-blue-400">🔵 Faible (P4)</td>
                    <td className="border border-border p-3">Incident mineur, pas d'impact utilisateur</td>
                    <td className="border border-border p-3">
                      <ul className="list-disc ml-4 text-sm">
                        <li>Email de phishing signalé</li>
                        <li>Fausse alerte confirmée</li>
                        <li>Mise à jour de sécurité requise</li>
                      </ul>
                    </td>
                    <td className="border border-border p-3 font-bold">≤72h</td>
                  </tr>
                </tbody>
              </table>
            </section>

            {/* Response Procedure */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Clock className="h-6 w-6" />
                5. Procédure de Réponse aux Incidents
              </h2>

              <div className="space-y-6">
                {/* Phase 1 */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                    Détection et Signalement
                  </h3>
                  <ul>
                    <li>Tout incident suspecté doit être signalé immédiatement à <strong>{securityEmail}</strong></li>
                    <li>Le signalement peut être fait par email, chat interne ou téléphone</li>
                    <li>Le signalement doit inclure : date/heure, description, systèmes affectés, actions déjà prises</li>
                    <li>Ne PAS tenter de corriger l'incident sans validation du Responsable Sécurité</li>
                  </ul>
                </div>

                {/* Phase 2 */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
                    Triage et Classification
                  </h3>
                  <ul>
                    <li>Évaluer la nature et la portée de l'incident</li>
                    <li>Classifier selon le niveau de gravité (P1-P4)</li>
                    <li>Identifier les systèmes, données et utilisateurs impactés</li>
                    <li>Déterminer si des données personnelles sont concernées (RGPD)</li>
                    <li>Activer l'équipe de réponse appropriée selon le niveau</li>
                  </ul>
                </div>

                {/* Phase 3 */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
                    Confinement
                  </h3>
                  <ul>
                    <li>Isoler les systèmes compromis (bloquer accès réseau, désactiver comptes)</li>
                    <li>Préserver les preuves (captures logs, snapshots)</li>
                    <li>Révoquer les tokens/sessions suspects</li>
                    <li>Activer les protections supplémentaires si nécessaire (WAF, rate limiting)</li>
                    <li>Documenter toutes les actions prises avec horodatage</li>
                  </ul>
                </div>

                {/* Phase 4 */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">4</span>
                    Investigation et Analyse
                  </h3>
                  <ul>
                    <li>Analyser les logs (audit_logs, auth logs, edge function logs)</li>
                    <li>Identifier le vecteur d'attaque et la chronologie</li>
                    <li>Évaluer l'étendue de la compromission</li>
                    <li>Identifier les données potentiellement exfiltrées</li>
                    <li>Déterminer la cause racine (root cause)</li>
                  </ul>
                </div>

                {/* Phase 5 */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">5</span>
                    Éradication et Remédiation
                  </h3>
                  <ul>
                    <li>Supprimer le vecteur d'attaque (malware, backdoor, compte compromis)</li>
                    <li>Appliquer les correctifs de sécurité nécessaires</li>
                    <li>Réinitialiser les credentials exposés</li>
                    <li>Renforcer les contrôles de sécurité (RLS policies, validation inputs)</li>
                    <li>Déployer les correctifs dans tous les environnements</li>
                  </ul>
                </div>

                {/* Phase 6 */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">6</span>
                    Restauration
                  </h3>
                  <ul>
                    <li>Restaurer les systèmes depuis des sauvegardes saines si nécessaire</li>
                    <li>Valider l'intégrité des données restaurées</li>
                    <li>Remettre progressivement les services en production</li>
                    <li>Surveiller étroitement les systèmes restaurés</li>
                    <li>Confirmer le retour à la normale</li>
                  </ul>
                </div>

                {/* Phase 7 */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm">7</span>
                    Revue Post-Incident
                  </h3>
                  <ul>
                    <li>Organiser une réunion de retour d'expérience (REX) dans les 5 jours</li>
                    <li>Documenter la chronologie complète de l'incident</li>
                    <li>Identifier les leçons apprises et les axes d'amélioration</li>
                    <li>Mettre à jour les procédures et contrôles si nécessaire</li>
                    <li>Archiver le rapport d'incident</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Communication Plan */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">6. Plan de Communication</h2>

              <h3 className="text-xl font-medium mt-6 mb-3">6.1 Communication Interne</h3>
              <ul>
                <li>L'équipe de réponse est informée immédiatement via canal sécurisé</li>
                <li>La direction est informée pour les incidents P1 et P2</li>
                <li>Les communications internes sont factuelles et confidentielles</li>
              </ul>

              <h3 className="text-xl font-medium mt-6 mb-3">6.2 Communication aux Autorités (RGPD)</h3>
              <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <p className="font-semibold text-amber-800 dark:text-amber-200">
                  En cas de violation de données personnelles :
                </p>
                <ul className="text-amber-700 dark:text-amber-300 mt-2">
                  <li>Notification à la CNIL dans les <strong>72 heures</strong> si risque pour les droits des personnes</li>
                  <li>Documentation de la violation dans le registre interne</li>
                  <li>Le DPO coordonne la notification via le portail CNIL</li>
                </ul>
              </div>

              <h3 className="text-xl font-medium mt-6 mb-3">6.3 Communication aux Utilisateurs</h3>
              <ul>
                <li>Les utilisateurs sont notifiés si leurs données sont compromises</li>
                <li>La notification inclut : nature de l'incident, données concernées, mesures prises, recommandations</li>
                <li>Communication via email et/ou notification in-app</li>
                <li>Un point de contact dédié est fourni pour les questions</li>
              </ul>

              <h3 className="text-xl font-medium mt-6 mb-3">6.4 Communication Publique</h3>
              <ul>
                <li>Toute communication publique est validée par la direction</li>
                <li>Communication factuelle, pas de spéculation</li>
                <li>Mise à jour régulière sur l'état de résolution</li>
              </ul>
            </section>

            {/* SaaS Specific Considerations */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">7. Considérations Spécifiques SaaS</h2>
              
              <h3 className="text-xl font-medium mt-6 mb-3">7.1 Incidents liés aux sous-traitants</h3>
              <ul>
                <li><strong>Supabase :</strong> Surveiller le status page, contacter le support si incident détecté</li>
                <li><strong>Cloudflare :</strong> Vérifier les alertes CDN, activer les règles de protection si attaque</li>
                <li><strong>Stripe :</strong> Vérifier les webhooks, contacter Stripe en cas de fraude détectée</li>
                <li><strong>Google APIs :</strong> Surveiller les quotas et révocations d'accès OAuth</li>
              </ul>

              <h3 className="text-xl font-medium mt-6 mb-3">7.2 Incidents OAuth/API</h3>
              <ul>
                <li>Tokens Gmail/Dropbox compromis : révocation immédiate via table connected_accounts</li>
                <li>Abus d'API : activation du rate limiting, blocage des IPs suspectes</li>
                <li>Invalidation de tous les refresh tokens si compromission généralisée</li>
              </ul>

              <h3 className="text-xl font-medium mt-6 mb-3">7.3 Sauvegarde et Restauration</h3>
              <ul>
                <li>Supabase maintient des sauvegardes automatiques quotidiennes</li>
                <li>Point-in-time recovery disponible pour les incidents base de données</li>
                <li>Les fichiers Storage sont répliqués géographiquement</li>
              </ul>
            </section>

            {/* Reporting Template */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="h-6 w-6" />
                8. Template de Signalement d'Incident
              </h2>

              <div className="bg-muted rounded-lg p-4 font-mono text-sm">
                <p><strong>📋 SIGNALEMENT D'INCIDENT DE SÉCURITÉ</strong></p>
                <br />
                <p><strong>Date/Heure de détection :</strong> [YYYY-MM-DD HH:MM]</p>
                <p><strong>Signalé par :</strong> [Nom / Email]</p>
                <p><strong>Classification initiale :</strong> [P1/P2/P3/P4]</p>
                <br />
                <p><strong>Description de l'incident :</strong></p>
                <p>[Description détaillée de ce qui s'est passé]</p>
                <br />
                <p><strong>Systèmes affectés :</strong></p>
                <p>[Liste des systèmes, URLs, fonctionnalités]</p>
                <br />
                <p><strong>Données potentiellement impactées :</strong></p>
                <p>[Types de données, estimation du nombre d'utilisateurs]</p>
                <br />
                <p><strong>Actions déjà prises :</strong></p>
                <p>[Liste des actions immédiates effectuées]</p>
                <br />
                <p><strong>Preuves collectées :</strong></p>
                <p>[Logs, captures d'écran, etc.]</p>
                <br />
                <p><strong>Envoyez ce formulaire à :</strong> {securityEmail}</p>
              </div>
            </section>

            {/* Policy Compliance */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">9. Conformité et Audit</h2>
              
              <h3 className="text-xl font-medium mt-6 mb-3">9.1 Vérification de la conformité</h3>
              <ul>
                <li>Revue annuelle de cette politique par le Responsable Sécurité</li>
                <li>Test de la procédure via exercice de simulation (tabletop) annuel</li>
                <li>Audit des logs et de la détection des incidents trimestriel</li>
              </ul>

              <h3 className="text-xl font-medium mt-6 mb-3">9.2 Exceptions</h3>
              <p>
                Toute exception à cette politique doit être approuvée par écrit par le Responsable Sécurité 
                et documentée avec justification.
              </p>

              <h3 className="text-xl font-medium mt-6 mb-3">9.3 Non-conformité</h3>
              <p>
                Le non-respect de cette politique peut entraîner des mesures disciplinaires, 
                pouvant aller jusqu'à la résiliation du contrat ou de l'accès au service.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">10. Contacts d'Urgence</h2>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <h3 className="font-semibold text-red-800 dark:text-red-200 mb-2">🚨 Urgence Sécurité</h3>
                  <p className="text-red-700 dark:text-red-300">
                    <a href={`mailto:${securityEmail}`} className="underline">{securityEmail}</a>
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                    Pour tout incident P1/P2 ou suspicion de compromission
                  </p>
                </div>

                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">📋 Protection des Données</h3>
                  <p className="text-blue-700 dark:text-blue-300">
                    <a href={`mailto:${dpoEmail}`} className="underline">{dpoEmail}</a>
                  </p>
                  <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                    Pour les questions RGPD et violations de données
                  </p>
                </div>
              </div>
            </section>

          </article>

          <div className="mt-8 pt-8 border-t text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} {companyName}. Tous droits réservés.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default IncidentResponsePolicy;
